import React from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'

export interface PageHeaderProps {
  title: React.ReactNode
  /** Short context shown beside the title ("25 people · 10 companies"). */
  subtitle?: React.ReactNode
  /** @deprecated The v2 header has no kicker; kept so older callers compile. */
  eyebrow?: React.ReactNode
  /** Right-aligned action pills. Buttons inside are sized to the bar. */
  actions?: React.ReactNode
  /** Breadcrumb back to a parent page, e.g. Customers / Janet Burns. Pass
   *  `onClick` instead of `to` when the parent is the same route in another
   *  state (the Workflows list behind its editor). */
  back?: { to?: string; onClick?: () => void; label: React.ReactNode }
  className?: string
}

/**
 * PageHeader — the one header every page uses.
 *
 * Desktop: a 60px sticky bar (16px title, muted subtitle, compact pill
 * actions) that runs edge to edge across the page and stays put while the
 * page scrolls, so the page's main action is always in reach.
 * Phones: a large title in the page flow — the fixed app bar above it
 * already owns the top of the screen.
 *
 * Place it first inside `.page-container`; it reads the container's padding
 * variables to break out to the edges.
 */
export function PageHeader({ title, subtitle, actions, back, className }: PageHeaderProps) {
  return (
    <header className={cn('ds-page-h', className)}>
      <div className="ds-page-h-t">
        {back && (
          <>
            {back.to
              ? <Link to={back.to} className="ds-page-h-crumb">{back.label}</Link>
              : <button type="button" onClick={back.onClick} className="ds-page-h-crumb">{back.label}</button>}
            <span className="ds-page-h-sep" aria-hidden>/</span>
          </>
        )}
        <h1>{title}</h1>
        {subtitle && <span className="ds-page-h-sub">{subtitle}</span>}
      </div>
      {actions && <div className="ds-page-h-acts">{actions}</div>}
    </header>
  )
}
